import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Returns a handler that navigates to the previous screen the user was on,
 * falling back to `fallback` when there's no in-app history to go back to.
 *
 * React Router records a monotonically increasing `idx` in `history.state`
 * for every navigation it owns. When `idx > 0` we know the user reached the
 * current page from somewhere else inside the app, so `navigate(-1)` returns
 * them there (the Grid, Command Center, a search result, etc.) instead of a
 * hardcoded list. On a fresh load / deep link / refresh, `idx` is 0 (or
 * absent) and going back would leave the app entirely, so we route to the
 * section fallback instead.
 */
export function useSmartBack(fallback: string): () => void {
    const navigate = useNavigate();
    return () => {
        const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (idx > 0) {
            navigate(-1);
        } else {
            navigate(fallback);
        }
    };
}

/**
 * Back button whose label matches what it actually does. When there's in-app
 * history to return to (the common case) it navigates back and reads simply
 * "Back" — the previous screen may have been the Grid, a search result, the
 * Command Center, etc., so a hardcoded "Back to jobs" was misleading. Only on a
 * fresh load / deep link (no history) does it fall back to a section route, and
 * there we show the contextual `fallbackLabel` ("Back to jobs") because that's
 * genuinely where the button goes.
 *
 * Pass the page's existing icon via `icon` and its existing class via
 * `className` so the visual stays identical.
 */
export function BackLink({
    fallback,
    fallbackLabel,
    className,
    icon,
}: {
    fallback: string;
    /** Label shown only when there's no history (deep link), e.g. "Back to jobs". */
    fallbackLabel: string;
    className?: string;
    icon?: ReactNode;
}) {
    const navigate = useNavigate();
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    const goesBack = idx > 0;
    const onClick = () => (goesBack ? navigate(-1) : navigate(fallback));
    return (
        <button type="button" onClick={onClick} className={className}>
            {icon}
            {goesBack ? "Back" : fallbackLabel}
        </button>
    );
}
